import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common.js';

// ============================================================================
// Produkt-News (PV-037) — docs/features/hilfe-und-news/
// ============================================================================

export const NewsSpracheSchema = z.enum(['de', 'en']);
export type NewsSprache = z.infer<typeof NewsSpracheSchema>;

export const NEWS_TITEL_MAX = 200;
export const NEWS_TEXT_MAX = 20_000;

export const ProduktNewsSchema = z.object({
  news_id: UuidSchema,
  titel_de: z.string(),
  titel_en: z.string(),
  /** Markdown */
  text_de: z.string(),
  /** Markdown */
  text_en: z.string(),
  /** null = Entwurf, für Nutzer unsichtbar */
  veroeffentlicht_am: IsoDateTimeSchema.nullable(),
  erstellt_am: IsoDateTimeSchema,
});
export type ProduktNews = z.infer<typeof ProduktNewsSchema>;

const gefuellt = (s: string): boolean => s.trim().length > 0;

/**
 * Anlegen/Ändern (Admin). Leere Strings sind erlaubt — das Formular kennt nur leer/gefüllt —,
 * aber mindestens EINE Sprache braucht Titel UND Text. Nur Leerzeichen gelten als leer
 * (Muster „Leerstring schaltet Filter ab").
 */
export const NewsEntwurfSchema = z
  .object({
    titel_de: z.string().max(NEWS_TITEL_MAX),
    titel_en: z.string().max(NEWS_TITEL_MAX),
    text_de: z.string().max(NEWS_TEXT_MAX),
    text_en: z.string().max(NEWS_TEXT_MAX),
  })
  .refine(
    (n) =>
      (gefuellt(n.titel_de) && gefuellt(n.text_de)) ||
      (gefuellt(n.titel_en) && gefuellt(n.text_en)),
    { message: 'news_mindestens_eine_sprache' },
  );
export type NewsEntwurf = z.infer<typeof NewsEntwurfSchema>;

/** Veröffentlichen: alle vier Texte gefüllt (serverseitig gegen den gespeicherten Datensatz). */
export const NewsVeroeffentlichbarSchema = z.object({
  titel_de: z.string().trim().min(1),
  titel_en: z.string().trim().min(1),
  text_de: z.string().trim().min(1),
  text_en: z.string().trim().min(1),
});

/** POST /v1/admin/news/uebersetzen — übersetzt `von` in die jeweils andere Sprache. */
export const NewsUebersetzenRequestSchema = z.object({
  von: NewsSpracheSchema,
  titel: z.string().trim().min(1).max(NEWS_TITEL_MAX),
  text: z.string().trim().min(1).max(NEWS_TEXT_MAX),
});
export type NewsUebersetzenRequest = z.infer<typeof NewsUebersetzenRequestSchema>;

/**
 * Modellantwort der Übersetzung. Dieselben Obergrenzen wie beim Speichern: ein zu langer
 * Vorschlag wird serverseitig abgewiesen (fail-closed), statt im Formular still „Speichern"
 * zu sperren.
 */
export const NewsUebersetzenResponseSchema = z.object({
  titel: z.string().trim().min(1).max(NEWS_TITEL_MAX),
  text: z.string().trim().min(1).max(NEWS_TEXT_MAX),
});
export type NewsUebersetzenResponse = z.infer<typeof NewsUebersetzenResponseSchema>;

/** GET /v1/news — nur veröffentlichte, neueste zuerst; beide Sprachen, die Oberfläche wählt. */
export const NewsListeResponseSchema = z.object({
  items: z.array(ProduktNewsSchema),
  /** „News gelesen bis"; ohne gespeicherten Wert die Kontoanlage. */
  gelesen_bis: IsoDateTimeSchema,
});
export type NewsListeResponse = z.infer<typeof NewsListeResponseSchema>;

/** POST /v1/news/gelesen */
export const NewsGelesenResponseSchema = z.object({ gelesen_bis: IsoDateTimeSchema });
export type NewsGelesenResponse = z.infer<typeof NewsGelesenResponseSchema>;
